/**
 * Essentials24 Animations & Micro-Interactions
 * Scroll-reveal, counters, hero entrance, ripple, marquee ticker
 */

(function () {
  'use strict';

  // ================================================
  // Utility: debounce
  // ================================================
  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn.bind(this, arguments), ms);
    };
  }

  // ================================================
  // T53: Scroll-Reveal with IntersectionObserver
  // ================================================
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ps-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });

    document.querySelectorAll('.ps-reveal').forEach(function (el) {
      observer.observe(el);
    });

    // Also animate children with stagger
    var childObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var children = entry.target.querySelectorAll('.ps-reveal-child');
          children.forEach(function (child, i) {
            setTimeout(function () {
              child.classList.add('ps-revealed');
            }, i * 80);
          });
          childObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -30px 0px'
    });

    document.querySelectorAll('.ps-reveal-container').forEach(function (el) {
      childObserver.observe(el);
    });
  }

  // ================================================
  // T54: Hero Entrance Animation (staggered fadeIn)
  // ================================================
  function initHeroEntrance() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var hero = document.querySelector('.essentials24-hero');
    if (!hero) return;

    var els = hero.querySelectorAll('.essentials24-hero__heading, .essentials24-hero__subheading, .essentials24-hero__button, .essentials24-hero__badge');
    els.forEach(function (el, i) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      setTimeout(function () {
        el.style.opacity = '';
        el.style.transform = '';
      }, 200 + i * 120);
    });
  }

  // ================================================
  // T56: Announcement Bar Marquee Ticker
  // ================================================
  function initAnnouncementMarquee() {
    var bar = document.querySelector('.essentials24-announcement-bar__messages, .announcement-bar__message');
    if (!bar || bar.querySelector('.ps-marquee-track')) return;

    var items = bar.querySelectorAll('p, span, .announcement-bar__link');
    if (items.length < 2) return;

    // wrap existing items in a marquee track
    var track = document.createElement('div');
    track.className = 'ps-marquee-track';
    track.style.cssText = 'display:flex;gap:4rem;animation:ps-marquee 20s linear infinite;white-space:nowrap;';

    items.forEach(function (item) {
      var clone = item.cloneNode(true);
      track.appendChild(clone);
    });
    // duplicate for seamless loop
    items.forEach(function (item) {
      var clone = item.cloneNode(true);
      track.appendChild(clone);
    });

    bar.style.overflow = 'hidden';
    bar.innerHTML = '';
    bar.appendChild(track);

    // inject keyframes
    if (!document.getElementById('ps-marquee-style')) {
      var style = document.createElement('style');
      style.id = 'ps-marquee-style';
      style.textContent = '@keyframes ps-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }';
      document.head.appendChild(style);
    }
  }

  // ================================================
  // T57: Button Ripple Effect
  // ================================================
  function initButtonRipple() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.button, .btn, .quick-add__submit, .essentials24-pack-card__add-all-btn, .essentials24-hero__button');
      if (!btn) return;

      var ripple = document.createElement('span');
      var rect = btn.getBoundingClientRect();
      var size = Math.max(rect.width, rect.height);
      var x = e.clientX - rect.left - size / 2;
      var y = e.clientY - rect.top - size / 2;

      ripple.style.cssText = [
        'position:absolute',
        'width:' + size + 'px',
        'height:' + size + 'px',
        'left:' + x + 'px',
        'top:' + y + 'px',
        'background:rgba(255,255,255,0.3)',
        'border-radius:50%',
        'transform:scale(0)',
        'animation:ps-ripple 0.5s ease-out forwards',
        'pointer-events:none',
        'z-index:10'
      ].join(';');

      var prevPosition = btn.style.position;
      if (!prevPosition || prevPosition === 'static') {
        btn.style.position = 'relative';
      }
      btn.style.overflow = 'hidden';
      btn.appendChild(ripple);

      setTimeout(function () {
        ripple.remove();
        if (!prevPosition) btn.style.position = '';
      }, 600);
    });

    if (!document.getElementById('ps-ripple-style')) {
      var style = document.createElement('style');
      style.id = 'ps-ripple-style';
      style.textContent = '@keyframes ps-ripple { to { transform: scale(2.5); opacity: 0; } }';
      document.head.appendChild(style);
    }
  }

  // ================================================
  // T57: ATC Feedback (visual confirmation)
  // ================================================
  function initATCFeedback() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.quick-add__submit, .product-form__submit');
      if (!btn) return;

      var originalText = btn.querySelector('span:not(.loading__spinner):not(.svg-wrapper)');
      if (!originalText) return;
      var orig = originalText.textContent;
      setTimeout(function () {
        if (originalText.textContent !== orig) return; // already changed by Shopify
        originalText.textContent = '✓ Hinzugefügt!';
        btn.style.background = '#5a8a6f';
        setTimeout(function () {
          originalText.textContent = orig;
          btn.style.background = '';
        }, 1800);
      }, 400);
    });
  }

  // ================================================
  // T58: Animated Trust Counter (count-up on scroll-in)
  // ================================================
  function initAnimatedCounters() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var counters = document.querySelectorAll('[data-count-up]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseInt(el.getAttribute('data-count-up'), 10);
        var suffix = el.getAttribute('data-count-suffix') || '';
        var duration = 1400;
        var start = 0;
        var startTime = null;

        function step(ts) {
          if (!startTime) startTime = ts;
          var progress = Math.min((ts - startTime) / duration, 1);
          var eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = Math.floor(eased * target).toLocaleString('de-DE') + suffix;
          if (progress < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
        observer.unobserve(el);
      });
    }, { threshold: 0.5 });

    counters.forEach(function (el) { observer.observe(el); });
  }

  // ================================================
  // T59: Image Blur-Up Loading
  // ================================================
  function initImageBlurUp() {
    var images = document.querySelectorAll('.essentials24-bestseller-card__img, .essentials24-category-card__background-image');
    images.forEach(function (img) {
      if (img.complete) return;
      img.style.filter = 'blur(8px)';
      img.style.transition = 'filter 0.4s ease';
      img.addEventListener('load', function () {
        img.style.filter = '';
      }, { once: true });
    });
  }

  // ================================================
  // T55: Collections Nav hover enhancement
  // (CSS handles the rotate + glow, JS adds data attr for targeting)
  // ================================================
  function initCollectionCardHover() {
    document.querySelectorAll('.essentials24-collection-card').forEach(function (card) {
      card.setAttribute('data-ps-card', 'collection');
    });
  }

  // ================================================
  // Add ps-reveal class to all major sections
  // ================================================
  function addRevealClasses() {
    var sections = [
      '.essentials24-collections',
      '.essentials24-collections-icon-nav',
      '.essentials24-mini-trust',
      '.essentials24-bestseller',
      '.featured-collection',
      '.essentials24-testimonials',
      '.essentials24-recommended-packs',
      '.essentials24-image-text',
      '.essentials24-category-showcase',
      '.essentials24-usps',
      '.essentials24-blog-teaser'
    ];

    sections.forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el && !el.classList.contains('ps-reveal')) {
        el.classList.add('ps-reveal');
      }
    });

    // Add stagger containers
    var grids = document.querySelectorAll(
      '.essentials24-collections__grid, .essentials24-collections-icon-nav__grid, .essentials24-bestseller__grid, .essentials24-recommended-packs__grid, .essentials24-usps__grid, .essentials24-blog-teaser__grid'
    );
    grids.forEach(function (grid) {
      grid.classList.add('ps-reveal-container');
      grid.querySelectorAll(':scope > *').forEach(function (child) {
        child.classList.add('ps-reveal-child');
      });
    });
  }

  // ================================================
  // T61: Toast Notification System
  // ================================================
  function initToastSystem() {
    // Create toast container
    if (!document.getElementById('ps-toast-container')) {
      var container = document.createElement('div');
      container.id = 'ps-toast-container';
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('aria-atomic', 'false');
      document.body.appendChild(container);
    }

    window.PSToast = {
      show: function (opts) {
        var container = document.getElementById('ps-toast-container');
        if (!container) return;

        var toast = document.createElement('div');
        toast.className = 'ps-toast';
        toast.innerHTML =
          (opts.image ? '<img src="' + opts.image + '" class="ps-toast__img" alt="" loading="lazy">' : '') +
          '<div class="ps-toast__body">' +
            '<p class="ps-toast__title">' + (opts.title || 'In den Warenkorb gelegt') + '</p>' +
            (opts.subtitle ? '<p class="ps-toast__subtitle">' + opts.subtitle + '</p>' : '') +
          '</div>' +
          '<button class="ps-toast__close" aria-label="Schließen">✕</button>';

        container.appendChild(toast);

        // Animate in
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            toast.classList.add('ps-toast--visible');
          });
        });

        // Close button
        toast.querySelector('.ps-toast__close').addEventListener('click', function () {
          dismissToast(toast);
        });

        // Auto dismiss after 3.5s
        setTimeout(function () { dismissToast(toast); }, 3500);
      }
    };

    function dismissToast(toast) {
      toast.classList.remove('ps-toast--visible');
      toast.classList.add('ps-toast--hiding');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 350);
    }

    // Hook into Shopify Cart AJAX events
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-atc-trigger], .product-form__submit, .essentials24-quick-add-btn');
      if (!btn) return;
      var card = btn.closest('.essentials24-product-card, .card-wrapper, .essentials24-bestseller-card');
      var title = 'In den Warenkorb gelegt';
      var img = null;
      if (card) {
        var titleEl = card.querySelector('.essentials24-product-card__title, .card__heading, .product-card__name');
        if (titleEl) title = titleEl.textContent.trim();
        var imgEl = card.querySelector('img');
        if (imgEl) img = imgEl.src.replace(/(_\d+x\d*)?\.[^?]+/, '_80x80.webp');
      }
      // Delay slightly to fire after cart add
      setTimeout(function () {
        if (window.PSToast) {
          window.PSToast.show({ title: title, subtitle: '✓ Erfolgreich hinzugefügt', image: img });
        }
      }, 400);
    });
  }

  // ================================================
  // T62: Sticky Mobile Buy-Bar on PDP
  // ================================================
  function initStickyMobileBuyBar() {
    var bar = document.querySelector('.essentials24-sticky-add-to-cart');
    if (!bar) return;
    var trigger = document.querySelector('.product-form, .product__info-wrapper');
    if (!trigger) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          bar.classList.add('essentials24-sticky-add-to-cart--visible');
        } else {
          bar.classList.remove('essentials24-sticky-add-to-cart--visible');
        }
      });
    }, { threshold: 0.1, rootMargin: '-60px 0px 0px 0px' });

    observer.observe(trigger);
  }

  // ================================================
  // T64: Sticky Category Scroll-Nav
  // ================================================
  function initStickyCategoryNav() {
    var collectionsSection = document.querySelector('.essentials24-collections-section, [data-section-type="essentials24-collections"]');
    if (!collectionsSection) return;

    var existingNav = document.getElementById('ps-sticky-cat-nav');
    if (existingNav) return;

    // Build nav from collection cards
    var cards = document.querySelectorAll('.essentials24-collection-card');
    if (cards.length === 0) return;

    var nav = document.createElement('nav');
    nav.id = 'ps-sticky-cat-nav';
    nav.className = 'ps-sticky-cat-nav';
    nav.setAttribute('aria-label', 'Kategorien');

    var inner = document.createElement('div');
    inner.className = 'ps-sticky-cat-nav__inner page-width';

    cards.forEach(function (card) {
      var link = card.querySelector('a');
      var titleEl = card.querySelector('.essentials24-collection-card__title');
      if (!link || !titleEl) return;
      var pill = document.createElement('a');
      pill.href = link.href;
      pill.className = 'ps-sticky-cat-nav__pill';
      pill.textContent = titleEl.textContent.trim();
      inner.appendChild(pill);
    });

    nav.appendChild(inner);
    document.body.appendChild(nav);

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          nav.classList.add('ps-sticky-cat-nav--visible');
        } else {
          nav.classList.remove('ps-sticky-cat-nav--visible');
        }
      });
    }, { threshold: 0 });

    observer.observe(collectionsSection);
  }

  // ================================================
  // Init on DOM ready
  // ================================================
  function init() {
    addRevealClasses();
    initScrollReveal();
    initHeroEntrance();
    initButtonRipple();
    initATCFeedback();
    initAnimatedCounters();
    initImageBlurUp();
    initCollectionCardHover();
    initToastSystem();
    initStickyMobileBuyBar();
    initStickyCategoryNav();
    // Marquee: only init if the bar has multiple messages
    setTimeout(initAnnouncementMarquee, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
